// @flow

import AuthToken from '../entities/AuthToken';
import type { EntityFieldCollection } from '../entities/types';
import { setErrorAction } from './errors/actions';
import type { StoredEntity } from '../entities/abstract/StoredEntity';
import type { Suggestions } from '../ui/types';

//
// TYPES
//

export type RequestStatus = 'pending' | 'ok' | 'partial' | 'fail' | undefined;

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

//
// HELPERS
//

export function encodeQueryData(data) {
    const ret = [];
    for (const d in data) ret.push(`${encodeURIComponent(d)}=${encodeURIComponent(data[d])}`);
    return ret.join('&');
}

export function buildUrl(path: string) {
    const cleanRoot = getBackofficeRoot();
    const cleanPath = path.replace(/^\/+/, '');
    return `${cleanRoot}/${cleanPath}`;
}

export function getBackofficeRoot(): string {
    const configuredRoot = process.env.REACT_APP_BACKOFFICE_ROOT || '/api/backoffice';
    return resolveApiRoot(configuredRoot);
}

function resolveApiRoot(root: string): string {
    const trimmedRoot = root.replace(/\/+$/, '');

    if (typeof window === 'undefined') {
        return trimmedRoot;
    }

    try {
        const parsedUrl = new URL(trimmedRoot, window.location.origin);
        const currentHostname = window.location.hostname;

        if (LOCAL_HOSTNAMES.includes(parsedUrl.hostname) && !LOCAL_HOSTNAMES.includes(currentHostname)) {
            parsedUrl.hostname = currentHostname;
        }

        return `${parsedUrl.origin}${parsedUrl.pathname}`.replace(/\/+$/, '');
    } catch (error) {
        return trimmedRoot;
    }
}

//
// AUTH
//

export function getBearerAuthorizationHeader() {
    /*
     * Construct an Authorization header with a Bearer scheme and the token from local storage.
     */
    return {
        Authorization: `Bearer ${AuthToken.get()}`,
    };
}

export function constructBasicAuthHeader(email, password) {
    /*
     * Construct an Authorization header with a Basic scheme and b64-encoded email and password.
     */
    return {
        Authorization: `Basic ${btoa(`${email}:${password}`)}`,
    };
}

//
// PRE-PROCESSING
//

export function getJsonContentTypeHeader() {
    return {
        'Content-Type': 'application/json',
    };
}

// eslint-disable-next-line no-unused-vars
export function getJsonRequestBody(json) {
    return JSON.stringify(json);
}

export function getFileRequestBody(file) {
    const formData = new FormData();
    formData.append('file', file);
    return formData;
}

//
// POST-PROCESSING
//

export function processFetchErrors(promise): Promise {
    return promise.then((response) => {
        if (response.ok) {
            return response.json();
        }
        return response
            .json()
            .catch(() => {
                throw new Error(`Request failed with status ${response.status}. No message was available.`);
            })
            .then((json) => {
                throw new Error(`${response.status}: ${json.message}`);
            });
    });
}

//
// HTTP REQUEST WRAPPERS
//

export function jsonGetRequest(url, processErrors = true): Promise {
    const promise = fetch(buildUrl(url), {
        method: 'GET',
        headers: getBearerAuthorizationHeader(),
    });
    return processErrors ? processFetchErrors(promise) : promise;
}

export function jsonPatchRequest(url, body, processErrors = true): Promise {
    const promise = fetch(buildUrl(url), {
        method: 'PATCH',
        headers: { ...getBearerAuthorizationHeader(), ...getJsonContentTypeHeader() },
        body,
    });
    return processErrors ? processFetchErrors(promise) : promise;
}

export function filePutRequest(url, body, processErrors = true): Promise {
    const promise = fetch(buildUrl(url), {
        method: 'PUT',
        headers: getBearerAuthorizationHeader(),
        body,
    });
    return processErrors ? processFetchErrors(promise) : promise;
}

export function deleteRequest(url, processErrors = true): Promise {
    const promise = fetch(buildUrl(url), {
        method: 'DELETE',
        headers: { ...getBearerAuthorizationHeader() },
    });
    return processErrors ? processFetchErrors(promise) : promise;
}

export function jsonPostRequest(url, body, processErrors = true, extraHeaders: Object = {}): Promise {
    const promise = fetch(buildUrl(url), {
        method: 'POST',
        headers: {
            ...getBearerAuthorizationHeader(),
            ...getJsonContentTypeHeader(),
            ...extraHeaders,
        },
        body,
    });
    return processErrors ? processFetchErrors(promise) : promise;
}

/** Authenticated binary/text download (e.g. CSV reports). */
export function downloadAuthenticated(url: string, filename: string): Promise<void> {
    return fetch(buildUrl(url), {
        method: 'GET',
        headers: getBearerAuthorizationHeader(),
    }).then((response) => {
        if (!response.ok) {
            return response
                .json()
                .catch(() => {
                    throw new Error(`Download failed with status ${response.status}`);
                })
                .then((json) => {
                    throw new Error(`${response.status}: ${json.message || 'Download failed'}`);
                });
        }
        return response.blob().then((blob) => {
            const objectUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(objectUrl);
        });
    });
}

export function jsonPutRequest(url, body, processErrors = true): Promise {
    const promise = fetch(buildUrl(url), {
        method: 'PUT',
        headers: { ...getBearerAuthorizationHeader(), ...getJsonContentTypeHeader() },
        body,
    });
    return processErrors ? processFetchErrors(promise) : promise;
}

//
// COMMON REQUESTS
//

export function genericFetchSuggestions(
    dispatch: any,
    modeName: string,
    fields: EntityFieldCollection,
    receiveSuggestions: (string, Map, [[StoredEntity]]) => any,
) {
    const promises = [];

    const mappedFields = new Map(Object.entries(fields));

    mappedFields.forEach((field) => {
        const model = field.dataType.extraData.model();
        promises.push(jsonGetRequest(model.endpoints.list()));
    });

    Promise.all(promises)
        .then((values) => dispatch(receiveSuggestions(modeName, mappedFields, values)))
        .catch((error) => dispatch(setErrorAction(error.toString())));
}

export function genericProcessSuggestions(mappedFields: Map, values: [[StoredEntity]]): Suggestions {
    const suggestions = {};

    // For every field, get the corresponding suggestions by index
    // (we can do that because Map is ordered and it's used in fetchSuggestions)
    // Then deserialize every record
    let idx = 0;
    mappedFields.forEach((field, name) => {
        // eslint-disable-next-line new-cap
        const model = field.dataType.extraData.model();
        suggestions[name] = values[idx].map((value) => new model(value));
        idx += 1;
    });

    return suggestions;
}
